import React from "react";
import { DispatchProp } from "react-redux";
import { intersection, isEmpty, range } from "../../utils/lodash";
import { BaseControl, ControlProps } from "./BaseControl";
import { QuestionModel } from "../../models/QuestionModel";
import { CursorPosition } from "../../models/enums/CursorPosition";
import {
    getSettingString,
    getSettingBoolean,
    getSettingEnum,
    getSettingNumber,
    hasSetting,
} from "../../models/utilities/settingUtilities";
import { getChoiceWithValue, decimalPlaces } from "../../models/utilities/questionUtilities";
import { getRendererProps } from "../renderers/BaseRenderer";
import { translate } from "../../models/utilities/translationUtilities";
import { QuestionType } from "../../models/enums/QuestionType";
import SliderRenderer, { DiscreteValue } from "../renderers/v2/SliderRenderer";
import { ElementWithSettings } from "../../models/ElementWithSettings";
import { VariableModel } from "../../models/VariableModel";
import { ChoiceModel } from "../../models/ChoiceModel";
import { isNullOrEmpty } from "../../utils";
import { isQuestionValidatedServerSide } from "../../models/utilities/validationUtilities";
import { ValidationBuilder } from "../../models/builders/ValidationBuilder";
import { ValidationOptionsModel } from "../../models/ValidationOptionsModel";
import { ValidationResultModel } from "../../models/ValidationResultModel";

interface Props extends ControlProps, DispatchProp {}

interface State {
    value: number;
}

enum SliderType {
    Continuous = "Continuous",
    Discrete = "Discrete",
}

export class SliderControl extends BaseControl<Props, State> {
    trackRef: Element;

    constructor(props: Props) {
        super(props);
        this.state = { value: 0 };
    }

    initializeQuestion(question: QuestionModel, props: Props): void {
        const [variable] = question.variables;
        // TODO: Duplicated code, needs refactoring
        if (this.isDiscrete(props)) {
            const defaultChoice = this.getChoices(props).find((choice) =>
                this.isElementDefault(choice),
            );
            if (defaultChoice !== undefined) {
                this.setMentions(props, variable.name, (mentions) =>
                    mentions.set(defaultChoice.choiceValueCode),
                );
            }
        }
        super.initializeQuestion(question, props);
        this.setState({
            value: this.getValue(props),
        });
    }

    didUpdateMentions(question: QuestionModel, prevProps: Props) {
        super.didUpdateMentions(question, prevProps);
        // We should not change the slider's position if No Answer is checked.
        if (!this.noAnswer(this.props)) {
            this.setState({ value: this.getValue(this.props) });
        }
    }

    didUpdateVariables(sequencedVariables: VariableModel[], prevProps: Props) {
        super.didUpdateVariables(sequencedVariables, prevProps);
        const [variable] = sequencedVariables;
        const mentionValue = this.getMentionValue(this.props, variable.name);
        const shouldClearAnswer =
            !isNullOrEmpty(mentionValue) &&
            !variable.choiceList.some((choice) => choice.value === mentionValue);

        if (shouldClearAnswer) {
            this.onClearAnswer(this.props);
        }
    }

    getDisplayResetButtonSetting = (props: Props) =>
        getSettingBoolean(props.question, "DisplayResetButton");

    getCursorPositionSetting = (props: Props) =>
        getSettingEnum(props.question, CursorPosition, "CursorPosition");

    getDisplayTicksSetting = (props: Props) => getSettingBoolean(props.question, "DisplayTicks");

    getStepValueSetting = (props: Props) => {
        const defaultValue = 1;
        const stepSetting = getSettingNumber(props.question, "StepValue", defaultValue);
        return this.isDiscrete(props) || stepSetting === 0 ? defaultValue : stepSetting;
    };

    getTicksIntervalSetting = (props: Props) => {
        const defaultValue = 1;
        const intervalSetting = getSettingNumber(props.question, "TicksInterval", defaultValue);
        return this.isDiscrete(props) || intervalSetting === 0 ? defaultValue : intervalSetting;
    };

    getColumnNoAnswerLabel = (props: Props) => {
        const { variableNames, parent } = props.question;
        if (!parent || parent.type !== QuestionType.HybridGrid) {
            return undefined;
        }
        const column = parent.columns.find(
            (parentColumn) =>
                !isEmpty(
                    intersection(
                        variableNames,
                        parentColumn.variables.map((variable) => variable.name),
                    ),
                ),
        );
        return translate(column, "NoAnswerLabel", props.language);
    };

    getDisplayNoAnswerSetting = (props: Props) =>
        getSettingBoolean(props.question, "DisplayNoAnswer");

    getValue(props: Props) {
        const [variable] = props.question.variables;
        const mention = this.getMention(props, variable.name);
        if (this.noAnswer(props) || !mention) {
            return this.getDefaultValue(props);
        }

        // TODO: Duplicated code, needs refactoring
        if (this.isDiscrete(this.props)) {
            const choice = this.getChoices(props).find((c) => c.choiceValueCode === mention.value);
            if (choice) {
                return choice.value;
            }
            return this.getDefaultValue(props);
        }

        const [min, max] = this.getBoundaries(props);
        return Math.min(max, Math.max(min, Number.parseFloat(mention.value)));
    }

    getChoices(props: Props) {
        const [variable] = props.sequencedVariables;
        const choices = variable.choiceList
            .filter((choice) => this.isElementVisible(choice))
            .filter((choice) => this.hasChoiceValue(choice))
            .map((choice, index) => {
                const { elementId, name, value, translatedTexts, settings } = choice;
                return {
                    elementId,
                    name,
                    value: index + 1,
                    choiceValueCode: value,
                    translatedTexts,
                    settings,
                };
            });
        return choices;
    }

    getDiscreteValues(props: Props): { [value: string]: DiscreteValue } {
        const choices = this.getChoices(props);
        const discreteValues = choices.reduce((acc, next) => {
            const { value, elementId, name, choiceValueCode, translatedTexts } = next;
            return {
                ...acc,
                [value]: {
                    elementId,
                    name,
                    choiceValueCode,
                    translatedTexts,
                },
            };
        }, {});
        return discreteValues;
    }

    getBoundaries(props: Props) {
        const isDiscrete = this.isDiscrete(props);
        const [minValue, maxValue] = isDiscrete
            ? [1, this.getChoices(props).length]
            : [Number.parseFloat(props.min), Number.parseFloat(props.max)];

        const min = Number.isNaN(minValue) ? 0 : minValue;
        const max = Number.isNaN(maxValue) ? 0 : maxValue;

        // TODO: What to do with a discrete slider that has only ONE choice?
        const overrideMax = !isDiscrete && max <= min;
        return [min, overrideMax ? min + this.getStepValueSetting(props) : max];
    }

    isElementVisible(element: ElementWithSettings) {
        return !hasSetting(element, "Visible") || getSettingBoolean(element, "Visible");
    }

    isElementDefault(element: ElementWithSettings) {
        return getSettingBoolean(element, "Default");
    }

    hasChoiceValue(choice: ChoiceModel) {
        return !isNullOrEmpty(choice.value);
    }

    getDefaultValue(props: Props) {
        if (this.isDiscrete(props)) {
            const defaultChoice = this.getChoices(props).find((choice) =>
                this.isElementDefault(choice),
            );
            if (defaultChoice) {
                return defaultChoice.value;
            }
        }
        if (hasSetting(props.question, "DefaultValue")) {
            const defaultValue = getSettingNumber(props.question, "DefaultValue");
            if (defaultValue) {
                return defaultValue;
            }
        }
        const [min, max] = this.getBoundaries(props);
        switch (this.getCursorPositionSetting(props)) {
            case CursorPosition.Hidden:
            case CursorPosition.Middle: {
                const middle = (max - min) / 2;
                const percent = (middle - min) / (max - min);
                const step = this.getStepValueSetting(props);
                return step * Math.round((percent * (max - min)) / step) + min;
            }
            case CursorPosition.Right:
                return max;
            case CursorPosition.Left:
            default:
                return min;
        }
    }

    getDefaultPosition(props: Props) {
        switch (this.getCursorPositionSetting(props)) {
            case CursorPosition.Right:
                return 100;
            case CursorPosition.Hidden:
            case CursorPosition.Middle:
                return 50;
            case CursorPosition.Left:
            default:
                return 0;
        }
    }

    getTickValues(props: Props): number[] {
        const [min, max] = this.getBoundaries(props);
        const interval = this.getTicksIntervalSetting(props);

        const maxAmount = 100;
        const amount = (max - min + 1) / interval;
        const increment = amount > maxAmount ? (max - min + 1) / maxAmount : interval;

        const values = Array.from(range(-max, -min + 1, increment))
            .reverse()
            .map((value) => -value);

        return values[0] === min ? values : [min, ...values];
    }

    onChange(value: number) {
        const [variable] = this.props.question.variables;
        if (this.isDiscrete(this.props)) {
            const discreteValues = this.getDiscreteValues(this.props);
            const discreteValue = discreteValues[value];
            if (discreteValue) {
                const { choiceValueCode } = discreteValue;
                if (choiceValueCode !== undefined) {
                    this.setMentions(this.props, variable.name, (mentions) =>
                        mentions.set(choiceValueCode),
                    );
                }
            }
            return;
        }
        if (Number.isNaN(value)) {
            return;
        }
        this.setMentions(this.props, variable.name, (mentions) =>
            mentions.set(String(value.toFixed(this.getStepDecimalPlaces(this.props)))),
        );
    }

    onClearAnswer = (props: Props) => {
        // TODO: Duplicated code, needs refactoring
        if (this.isDiscrete(props)) {
            const defaultChoice = this.getChoices(props).find((choice) =>
                this.isElementDefault(choice),
            );
            if (defaultChoice) {
                this.onChange(defaultChoice.value);
            } else {
                this.clearMentions(props);
            }
        } else if (hasSetting(props.question, "DefaultValue")) {
            const defaultValue = this.getDefaultValue(props);
            this.onChange(defaultValue);
        } else {
            this.clearMentions(props);
        }
    };

    clearMentions = (props: Props) => {
        const [variable] = props.question.variables;
        this.setMentions(props, variable.name, (mentions) => mentions.clear());
    };

    onToggleNoAnswer = (props: Props) => {
        const { question } = props;
        const [variable] = question.variables;
        if (!hasSetting(question, "NoAnswerValue")) {
            return;
        }
        const value = this.getMentionValue(props);
        const noAnswerValue = getSettingString(question, "NoAnswerValue");
        if (value !== noAnswerValue) {
            this.setMentions(props, variable.name, (mentions) => mentions.set(noAnswerValue));
        } else {
            this.onClearAnswer(props);
        }
    };

    noAnswer(props: Props) {
        const [variable] = props.question.variables;
        const mention = this.getMention(props, variable.name);
        if (mention === undefined) {
            return false;
        }
        return (
            this.getDisplayNoAnswerSetting(props) &&
            mention.value === this.getNoAnswerValueSetting(props)
        );
    }

    getNoAnswerValueSetting = (props: Props) =>
        getSettingString(props.question, "NoAnswerValue", "0");

    getNoAnswerLabel = (props: Props) => {
        return (
            this.getColumnNoAnswerLabel(props) ||
            translate(props.question, "NoAnswerLabel", props.language) ||
            translate(props.theme, "SliderNoAnswer", props.language)
        );
    };

    onChangeNoAnswer = () => {
        this.setState((prevState, props) => {
            const [variable] = props.question.variables;
            const newState = { ...prevState };
            const noAnswerValue = this.getNoAnswerValueSetting(props);
            if (this.noAnswer(props)) {
                this.setMentions(props, variable.name, (mentions) => mentions.set(noAnswerValue));
            } else {
                this.setMentions(props, variable.name, (mentions) =>
                    mentions.set(String(newState.value)),
                );
            }
            return newState;
        });
    };

    displayValue = (props: Props, value: number) => {
        if (this.noAnswer(props)) {
            return undefined;
        }
        if (!this.isDiscrete(props)) {
            return String(value);
        }
        const choice = getChoiceWithValue(props.question, String(value));
        if (choice !== undefined) {
            return translate(choice, "TEXT", props.language);
        }
        return undefined;
    };

    isHybridGrid = (props: Props) => props.question.parent?.type === QuestionType.HybridGrid;

    hasImages = (props: Props) => {
        if (!this.isDiscrete(props)) {
            return false;
        }
        const {
            question: { variables },
        } = props;
        const [variable] = variables;
        const hasImages = variable?.choiceList
            .map((choice) => choice.translatedTexts?.["imageurl"])
            .some((entry) => Object.keys(entry ?? {}).some((key) => !isNullOrEmpty(entry[key])));
        return hasImages;
    };

    getSliderType = (props: Props): SliderType => {
        const { question } = props;
        const isHybridGrid = this.isHybridGrid(props);
        const [key, valueDiscrete] = isHybridGrid
            ? ["VarType", "0"]
            : ["Type", SliderType.Discrete];

        const sliderType = getSettingBoolean(question, key, valueDiscrete)
            ? SliderType.Discrete
            : SliderType.Continuous;
        return sliderType;
    };

    isDiscrete = (props: Props) => SliderType.Discrete === this.getSliderType(props);

    getStepDecimalPlaces = (props: Props): number => {
        const value = getSettingString(props.question, "StepValue");
        return decimalPlaces(value);
    };

    getTickDecimalPlaces = (props: Props): number => {
        const value = getSettingString(props.question, "TicksInterval");
        return decimalPlaces(value);
    };

    hasNoAnswer = (props: Props) => {
        const { question } = props;
        const [variable] = question.variables;
        return (
            this.getMentionValue(props, variable.name) ===
            getSettingString(props.question, "NoAnswerValue")
        );
    };

    hasValueSet = (props: Props) => {
        const { question } = props;
        const [variable] = question.variables;
        const mention = this.getMention(props, variable.name);
        return mention && this.isValueValid(props, mention.value) && !this.hasNoAnswer(props);
    };

    isValueValid = (props: Props, value: string) => {
        return !this.isDiscrete(props) || this.isChoiceVisible(value, props);
    };

    validate(options?: ValidationOptionsModel): ValidationResultModel {
        const { question, useClientValidation } = this.props;

        if (isQuestionValidatedServerSide(question.type, useClientValidation)) {
            return new ValidationBuilder().build();
        }
        return super.validate(options);
    }

    renderQuestion() {
        const { state, props } = this;
        const [variable] = props.question.variables;
        return (
            <SliderRenderer
                {...getRendererProps(props)}
                value={state.value}
                domain={this.getBoundaries(props)}
                hasValueSet={this.hasValueSet(props)}
                hasNoAnswer={this.hasNoAnswer(props)}
                noAnswerLabel={this.getNoAnswerLabel(props)}
                stepValue={this.getStepValueSetting(props)}
                onChange={([value]) => this.onChange(value)}
                displayClearButton={this.getDisplayResetButtonSetting(props)}
                onClearAnswer={() => this.onClearAnswer(props)}
                onToggleNoAnswer={() => this.onToggleNoAnswer(props)}
                displayTicks={this.getDisplayTicksSetting(props)}
                tickValues={this.getTickValues(props)}
                displayNoAnswer={this.getDisplayNoAnswerSetting(props)}
                isDiscrete={this.isDiscrete(props)}
                discreteValues={this.getDiscreteValues(props)}
                stepDecimalPlaces={this.getStepDecimalPlaces(props)}
                tickDecimalPlaces={this.getTickDecimalPlaces(props)}
                defaultPosition={this.getDefaultPosition(props)}
                hasImages={this.hasImages(props)}
                isHybridGrid={this.isHybridGrid(props)}
                elementId={variable.elementId}
                isDisabled={props.isDisabled}
            />
        );
    }
}
